import type Shipment from 'interfaces/PmParcel';
import { useEffect, useState } from 'preact/hooks';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import Footer from 'components/Footer';
import { FooterBanner } from 'components/FooterBanners';
import FocusManager from 'components/FocusManager';
import { agentService } from 'services/agent.services';
import { isReturnShipment } from 'utils/shipmentDisplay.utils';

import { useFocus } from 'contexts/FocusContext';
import { ForwardShipment } from '../ForwardShipment';
import Header from '../Header';
import { ReturnShipment } from '../ReturnShipment';
import { Skeleton } from '../Skeleton';
import { SubscriptionModal } from '../SubscriptionModal';
import { CurrentShipmentProvider } from '../../hooks/useCurrentShipment';
import { Container, HeaderWrapper } from './MerchantTrackingWidget.styles';

interface MerchantTrackingWidgetProps {
  data: Shipment[];
  isLoading: boolean;
  updateShipment: (shipment: Shipment) => void;
  reloadShipment: (shipmentPk: number) => void;
}

export const MerchantTrackingWidget = ({
  data,
  isLoading,
  updateShipment,
  reloadShipment,
}: MerchantTrackingWidgetProps): JSX.Element => {
  const [selectedShipment, setSelectedShipment] = useState<Shipment>(data[0]);
  const { organizationId } = useGlobalConfigContext();
  const hasMultipleShipments = data.length > 1;

  const { shouldFocus } = useFocus();

  useEffect(() => {
    const found = data.find((item) => item.poId === selectedShipment?.poId);
    setSelectedShipment(found || data[0]);
  }, [data]);

  const onSelectShipment = (shipment: Shipment) => {
    agentService.init(shipment.id);
    setSelectedShipment(shipment);
  };

  const renderContent = () => {
    // Use the Skeleton component to handle following cases
    // 1. Loading shipment
    // 2. Not found shipment
    if (isLoading || !selectedShipment) {
      return (
        <Skeleton isLoading={isLoading} isNoShipment={!selectedShipment} />
      );
    }

    return (
      <>
        <CurrentShipmentProvider
          shipment={selectedShipment}
          updateShipment={updateShipment}
          reloadShipment={reloadShipment}
        >
          {hasMultipleShipments && (
            <HeaderWrapper data-testid="merchant-tracking-header-wrapper">
              <Header
                shipments={data}
                selectedShipment={selectedShipment}
                onSelectShipment={onSelectShipment}
              />
            </HeaderWrapper>
          )}

          {isReturnShipment(selectedShipment) ? (
            <ReturnShipment
              key={selectedShipment.poId}
              hasMultipleShipments={hasMultipleShipments}
            />
          ) : (
            <ForwardShipment
              key={selectedShipment.poId}
              hasMultipleShipments={hasMultipleShipments}
            />
          )}
        </CurrentShipmentProvider>
        <FooterBanner shipment={selectedShipment} />
      </>
    );
  };

  return (
    <FocusManager shouldFocus={shouldFocus}>
      <Container id="portalWrapper">
        <SubscriptionModal
          organizationId={organizationId}
          selectedShipment={selectedShipment}
        />

        {renderContent()}

        <Footer />
      </Container>
    </FocusManager>
  );
};

export default MerchantTrackingWidget;
